/**
 * Lightweight client-side "writing helper" — no third party, no ML.
 * Returns an array of suggestions for plain text.
 */

const FILLER_WORDS = ["very", "really", "just", "actually", "literally", "basically", "kind of", "sort of", "definitely", "perhaps"];
const HEDGE_WORDS = ["maybe", "somewhat", "rather", "quite", "fairly", "slightly"];
const PASSIVE_REGEX = /\b(is|are|was|were|be|been|being|am|been)\s+(\w+(?:ed|en))\b/gi;
const DOUBLE_SPACE_REGEX = / {2,}/g;
const REPEATED_WORD_REGEX = /\b(\w+)\s+\1\b/gi;

export function analyzeText(plainText = "") {
  const text = (plainText || "").trim();
  const issues = [];

  // Word + char counts
  const words = text.split(/\s+/).filter(Boolean);
  const wordCount = words.length;
  const charCount = text.length;
  const sentences = text.split(/[.!?]+/).filter((s) => s.trim().length > 0);
  const sentenceCount = sentences.length;
  const avgWordsPerSentence = sentenceCount ? Math.round(wordCount / sentenceCount) : 0;

  // Long sentences
  sentences.forEach((s) => {
    const n = s.trim().split(/\s+/).filter(Boolean).length;
    if (n > 30) {
      issues.push({ severity: "warn", kind: "long-sentence", message: `Long sentence (${n} words). Consider breaking it up.`, snippet: s.trim().slice(0, 80) + (s.length > 80 ? "…" : "") });
    }
  });

  // Filler words
  const fillerHits = new Set();
  FILLER_WORDS.forEach((w) => {
    const re = new RegExp(`\\b${w}\\b`, "gi");
    if (re.test(text)) fillerHits.add(w);
  });
  if (fillerHits.size) {
    issues.push({
      severity: "info",
      kind: "filler",
      message: `Filler words to consider removing: ${[...fillerHits].join(", ")}`,
    });
  }

  // Hedge words
  const hedgeHits = new Set();
  HEDGE_WORDS.forEach((w) => {
    const re = new RegExp(`\\b${w}\\b`, "gi");
    if (re.test(text)) hedgeHits.add(w);
  });
  if (hedgeHits.size > 2) {
    issues.push({
      severity: "info",
      kind: "hedging",
      message: `Many hedging words (${[...hedgeHits].join(", ")}). Write with more conviction.`,
    });
  }

  // Passive voice
  const passiveMatches = text.match(PASSIVE_REGEX);
  if (passiveMatches && passiveMatches.length > 1) {
    issues.push({
      severity: "info",
      kind: "passive",
      message: `${passiveMatches.length} possible passive constructions. Active voice is often stronger.`,
    });
  }

  // Repeated adjacent words
  const repeats = [...text.matchAll(REPEATED_WORD_REGEX)].map((m) => m[1]).filter((w) => w.length > 2);
  if (repeats.length) {
    issues.push({
      severity: "warn",
      kind: "repeated",
      message: `Repeated word: "${repeats[0]}". Check for accidental duplication.`,
    });
  }

  // Double spaces
  if (DOUBLE_SPACE_REGEX.test(text)) {
    issues.push({ severity: "info", kind: "double-space", message: "Double spaces detected — collapse to single spaces." });
  }

  // Reading time
  const readingMinutes = Math.max(1, Math.round(wordCount / 220));

  return {
    wordCount,
    charCount,
    sentenceCount,
    avgWordsPerSentence,
    readingMinutes,
    issues,
  };
}
